import { defineStore } from 'pinia'

import { Approval, ApprovalApi } from '@/shared/apis/approvals'
import { Transaction } from '@/shared/models/Transaction'
import { Sign } from '@/shared/models/Sign'
import { useGlobals } from '@/popup/compositions/useGlobals'

type ApprovalDirectiveType = 'DELETE' | 'STASH'
/**
 * Represents a directive for processing an approval action.
 *
 * This type is used to instruct the `approvalStore` on what action to take regarding an approval.
 */
export type ApprovalDirective = {
  type: ApprovalDirectiveType
  approvalId: string
}

export type ApprovalsState = {
  /**
   * All pending approvals from ApprovalController. Sorted.
   */
  nonStashedApprovals: Approval<keyof ApprovalApi, unknown>[]
  /**
   * One of the sources for the number of "unread notifications" rendered on the badge of the extension.
   */
  profileApprovals: Approval<keyof ApprovalApi, unknown>[]
}

export const useApprovalsStore = defineStore('approvals', {
  state: () =>
    ({
      nonStashedApprovals: [],
      profileApprovals: [],
    }) as ApprovalsState,
  getters: {
    lastAddress: state => {
      for (const address of state.nonStashedApprovals) {
        if (address.type === 'sign') {
          return (address.requestData as Sign).profileAddress
        }
        if (address.type === 'transaction') {
          return (address.requestData as Transaction).from
        }
      }
    },
  },
  actions: {
    processDirective(directive?: ApprovalDirective) {
      if (!directive) {
        return
      }

      if (directive.type === 'DELETE') {
        this.deleteProfileApproval(directive.approvalId)
      } else if (directive.type === 'STASH') {
        this.stashApproval(directive.approvalId)
      }
    },
    getApproval(id: string | number | string[]) {
      const nonStashed = this.nonStashedApprovals.find(
        approval => approval.id.toString() === id.toString()
      )
      return (
        nonStashed ||
        this.profileApprovals.find(
          approval => approval.id.toString() === id.toString()
        )
      )
    },
    init(nonStashedApprovals: Approval<keyof ApprovalApi, unknown>[]) {
      this.nonStashedApprovals = nonStashedApprovals
    },
    setProfileApprovals(approvals: Approval<keyof ApprovalApi, unknown>[]) {
      this.profileApprovals = approvals
    },
    deleteApproval(requestId: string) {
      this.nonStashedApprovals.splice(
        this.nonStashedApprovals.findIndex(approval => {
          return approval.id === requestId
        }),
        1
      )
    },
    deleteProfileApproval(requestId: string) {
      console.log(
        'deleteProfileApproval called',
        requestId,
        this.nonStashedApprovals.length,
        this.profileApprovals.length
      )
      this.deleteApproval(requestId)
      this.profileApprovals.splice(
        this.profileApprovals.findIndex(approval => {
          return approval.id === requestId
        }),
        1
      )
      console.log(
        'deleteProfileApproval call end',
        this.nonStashedApprovals.length,
        this.profileApprovals.length
      )
    },
    stashApproval(requestId: string) {
      this.deleteApproval(requestId)
      this.profileApprovals = this.profileApprovals.map(approval =>
        approval.id === requestId ? { ...approval, isStashed: true } : approval
      )
    },
    addNonStashedApproval(approval: Approval<keyof ApprovalApi, unknown>) {
      this.nonStashedApprovals.push(approval)
    },
    addProfileApproval(approval: Approval<keyof ApprovalApi, unknown>) {
      this.profileApprovals.push(approval)
    },
    async handleApprovalCancelled(approvalId: string) {
      const globals = useGlobals()
      const path = globals?.router.currentRoute?.value?.path
      const currentPathApprovalId = path ? path.split('/').at(-1) : ''
      if (approvalId !== currentPathApprovalId) {
        globals?.router.push('/home')
      }
      this.deleteProfileApproval(approvalId)
    },
  },
})
